"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { config } from "@repo/config";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@ui/components/dialog";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import { Slider } from "@ui/components/slider";
import { Coins, ShoppingCart, Loader2 } from "lucide-react";

interface PaygCreditsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onConfirm: (credits: number) => void;
	isLoading?: boolean;
}

// Presets aligned with common use cases (15 = 1 report, 30 = 2 reports, etc.)
const CREDIT_PRESETS = [15, 30, 50, 100, 250];
const MAX_CREDITS = 500;

// Get config values
const paygConfig = config.payments.plans.paygCredits;
const MIN_CREDITS = paygConfig?.minPurchaseAmount ?? 15;
const DEFAULT_CREDITS = parseInt(process.env.NEXT_PUBLIC_DEFAULT_AMOUNT || "30");

export function PaygCreditsModal({
	open,
	onOpenChange,
	onConfirm,
	isLoading = false,
}: PaygCreditsModalProps) {
	const t = useTranslations();
	const [creditAmount, setCreditAmount] = useState(DEFAULT_CREDITS);
	const [customAmount, setCustomAmount] = useState("");
	const [isCustom, setIsCustom] = useState(false);

	const pricePerCredit = paygConfig?.prices?.[0]?.amount ?? 1;
	const effectiveAmount = isCustom ? parseInt(customAmount) || 0 : creditAmount;
	const totalPrice = effectiveAmount * pricePerCredit;
	const isValidAmount = effectiveAmount >= MIN_CREDITS;

	const handlePresetClick = (amount: number) => {
		setIsCustom(false);
		setCreditAmount(amount);
	};

	const handleCustomChange = (value: string) => {
		setIsCustom(true);
		setCustomAmount(value);
	};

	const handleSliderChange = (value: number) => {
		setIsCustom(false);
		setCreditAmount(value);
	};

	const handleConfirm = () => {
		if (isValidAmount) {
			onConfirm(effectiveAmount);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Coins className="h-5 w-5 text-primary" />
						{t("settings.billing.paygModal.title" as any)}
					</DialogTitle>
					<DialogDescription>
						{t("settings.billing.paygModal.description" as any)}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{/* Quick Select Presets */}
					<div className="space-y-2">
						<Label>{t("settings.billing.addCredits.quickSelect" as any)}</Label>
						<div className="flex flex-wrap gap-2">
							{CREDIT_PRESETS.map((preset) => (
								<Button
									key={preset}
									variant={!isCustom && creditAmount === preset ? "secondary" : "outline"}
									size="sm"
									onClick={() => handlePresetClick(preset)}
									disabled={isLoading}
								>
									<Coins className="h-3.5 w-3.5 mr-1.5" />
									{preset}
								</Button>
							))}
						</div>
					</div>

					{/* Range slider for fine-tuning */}
					<div className="space-y-3">
						<div className="flex items-center justify-between">
							<Label>{t("settings.billing.addCredits.selectAmount" as any)}</Label>
							<span className="text-sm font-medium text-primary">
								{effectiveAmount} credits
							</span>
						</div>
						<Slider
							value={[isCustom ? parseInt(customAmount) || MIN_CREDITS : creditAmount]}
							onValueChange={(value) => handleSliderChange(value[0])}
							min={MIN_CREDITS}
							max={MAX_CREDITS}
							step={5}
							disabled={isLoading}
							className="cursor-pointer"
						/>
						<div className="flex justify-between text-xs text-muted-foreground">
							<span>${MIN_CREDITS}</span>
							<span>${MAX_CREDITS}</span>
						</div>
					</div>

					{/* Custom Amount Input */}
					<div className="space-y-2">
						<Label htmlFor="customAmount">{t("settings.billing.addCredits.customAmount" as any)}</Label>
						<div className="flex items-center gap-2">
							<Input
								id="customAmount"
								type="number"
								min={MIN_CREDITS}
								max={MAX_CREDITS}
								placeholder={`Min ${MIN_CREDITS} credits`}
								value={isCustom ? customAmount : creditAmount.toString()}
								onChange={(e) => handleCustomChange(e.target.value)}
								disabled={isLoading}
								className="max-w-[150px]"
							/>
							<span className="text-sm text-muted-foreground">credits</span>
						</div>
					</div>

					{/* Summary */}
					<div className="rounded-lg bg-muted/50 p-4 space-y-2">
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">{t("settings.billing.addCredits.credits" as any)}</span>
							<span className="font-medium">{effectiveAmount.toLocaleString()}</span>
						</div>
						<div className="flex items-center justify-between text-sm">
							<span className="text-muted-foreground">{t("settings.billing.addCredits.pricePerCredit" as any)}</span>
							<span className="font-medium">${pricePerCredit.toFixed(2)}</span>
						</div>
						<div className="border-t pt-2 flex items-center justify-between">
							<span className="font-medium">{t("settings.billing.addCredits.total" as any)}</span>
							<span className="text-xl font-bold text-primary">
								${totalPrice.toFixed(2)}
							</span>
						</div>
					</div>

					{/* Validation message */}
					{!isValidAmount && effectiveAmount > 0 && (
						<p className="text-sm text-destructive">
							{t("settings.billing.addCredits.minimumRequired" as any, { amount: MIN_CREDITS })}
						</p>
					)}
				</div>

				{/* Actions */}
				<div className="flex gap-3 justify-end">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={isLoading}
					>
						{t("common.cancel" as any)}
					</Button>
					<Button
						onClick={handleConfirm}
						disabled={!isValidAmount || isLoading}
					>
						{isLoading ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								{t("settings.billing.addCredits.processing" as any)}
							</>
						) : (
							<>
								<ShoppingCart className="h-4 w-4 mr-2" />
								{t("settings.billing.paygModal.continueToCheckout" as any)}
							</>
						)}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

